import ApiServer from "../ApiServer.ts";
import * as amplify from "./amplify.ts";

type PushType = string;

type WebSocketPushMessage = InstanceType<typeof ApiServer.types.WebSocketPushMessage>;
type PushHandler = (msg: WebSocketPushMessage) => void;

type HandlerMap = Map<PushType, Set<PushHandler>>;

const NORMAL_RECONNECT_DELAY_MS = 1_000;
const ABNORMAL_RECONNECT_DELAY_MS = 30_000;
const URL_FETCH_RECONNECT_DELAY_MS = 5_000;
const PING_INTERVAL_MS = 50_000;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, Math.max(0, ms)));
}

function normalizePushTypes(pushTypes: PushType | PushType[]): PushType[] {
  return Array.isArray(pushTypes) ? pushTypes : [pushTypes];
}

class WebSocketPushClient {
  private ws: WebSocket | null = null;
  private handlers: HandlerMap = new Map();

  private connecting = false;
  private reconnectScheduled = false;
  private stopped = false;

  private readonly pingIntervalId: number | null;

  public readonly isActive: boolean;

  public constructor() {
    this.isActive =
      typeof window !== "undefined" && typeof window.WebSocket !== "undefined";

    if (!this.isActive) {
      this.pingIntervalId = null;
      return;
    }

    this.pingIntervalId = window.setInterval(() => {
      this.sendPing();
    }, PING_INTERVAL_MS);

    (
      window as typeof window & {
        emulateNotification?: (json: unknown) => void;
      }
    ).emulateNotification = (json: unknown) => {
      this.handleWebSocketNotif(json);
    };
  }

  public subscribe = (
    pushTypes: PushType | PushType[],
    handler: PushHandler,
  ): void => {
    if (!this.isActive || this.stopped) {
      return;
    }

    for (const pushType of normalizePushTypes(pushTypes)) {
      let set = this.handlers.get(pushType);
      if (!set) {
        set = new Set<PushHandler>();
        this.handlers.set(pushType, set);
      }
      set.add(handler);
    }

    void this.ensureConnectionSoon();
  };

  public unsubscribe = (
    pushTypes: PushType | PushType[],
    handler: PushHandler,
  ): void => {
    if (!this.isActive) {
      return;
    }

    for (const pushType of normalizePushTypes(pushTypes)) {
      const set = this.handlers.get(pushType);
      if (!set) {
        continue;
      }

      set.delete(handler);

      if (set.size === 0) {
        this.handlers.delete(pushType);
      }
    }

    if (!this.connectionNeeded()) {
      this.closeSocket("No more subscriptions");
    }
  };

  public stop(reason = "WebSocket client stopped"): void {
    if (!this.isActive) {
      return;
    }

    this.stopped = true;
    this.reconnectScheduled = false;
    this.connecting = false;

    this.closeSocket(reason);
    ApiServer.webjslog(reason);
  }

  private connectionNeeded(): boolean {
    for (const set of this.handlers.values()) {
      if (set.size > 0) {
        return true;
      }
    }
    return false;
  }

  private async ensureConnectionSoon(): Promise<void> {
    await sleep(0);
    await this.init();
  }

  private async init(): Promise<void> {
    if (!this.isActive || this.stopped || this.connecting || this.ws) {
      return;
    }

    if (!this.connectionNeeded()) {
      return;
    }

    this.connecting = true;

    try {
      const uri = await ApiServer.webgetWebSocketUrl();

      if (this.stopped || !this.connectionNeeded()) {
        return;
      }

      await this.openWebSocket(uri);
    } catch (error) {
      this.connecting = false;
      this.ws = null;

      ApiServer.webjslog(`webgetWebSocketUrl error: ${this.stringifyError(error)}`);

      if (!this.stopped && this.connectionNeeded()) {
        this.scheduleReconnect(URL_FETCH_RECONNECT_DELAY_MS);
      }
    }
  }

  private async openWebSocket(uri: string): Promise<void> {
    const socket = new WebSocket(uri);
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) {
        return;
      }

      this.connecting = false;
      this.reconnectScheduled = false;
    };

    socket.onmessage = (evt: MessageEvent<string>) => {
      try {
        const json = JSON.parse(evt.data);
        this.handleWebSocketNotif(json);
      } catch (error) {
        ApiServer.webjslog(
          `websocket message parse error: ${this.stringifyError(error)}`,
        );
      }
    };

    socket.onclose = (evt: CloseEvent) => {
      const isCurrentSocket = this.ws === socket;
      if (isCurrentSocket) {
        this.ws = null;
      }

      this.connecting = false;

      if (this.stopped || !this.connectionNeeded()) {
        return;
      }

      switch (evt.code) {
        case 1000:
        case 1001: {
          this.scheduleReconnect(NORMAL_RECONNECT_DELAY_MS);
          return;
        }

        case 1006: {
          this.scheduleReconnect(ABNORMAL_RECONNECT_DELAY_MS);
          return;
        }

        default: {
          this.stop(`websocket close error ${evt.code} ${evt.reason}`);
        }
      }
    };

    socket.onerror = () => {
      ApiServer.webjslog("websocket error");

      if (this.ws === socket) {
        this.ws = null;
      }

      this.connecting = false;

      try {
        socket.close();
      } catch {
        // ignore close errors
      }
    };
  }

  private scheduleReconnect(delayMs: number): void {
    if (
      !this.isActive ||
      this.stopped ||
      this.reconnectScheduled ||
      this.connecting ||
      this.ws ||
      !this.connectionNeeded()
    ) {
      return;
    }

    this.reconnectScheduled = true;

    window.setTimeout(async () => {
      this.reconnectScheduled = false;
      await this.init();
    }, delayMs);
  }

  private closeSocket(reason?: string): void {
    const socket = this.ws;
    this.ws = null;
    this.connecting = false;

    if (!socket) {
      return;
    }

    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      try {
        socket.close(1000, reason);
      } catch {
        // ignore close errors
      }
    }
  }

  private sendPing(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      this.ws.send("ping");
    } catch (error) {
      ApiServer.webjslog(`websocket ping error: ${this.stringifyError(error)}`);
    }
  }

  private handleWebSocketNotif(json: unknown): void {
    const msg = new ApiServer.types.WebSocketPushMessage(json);

    switch (msg.type) {
      case "sessionExpired": {
        this.handlers.clear();
        this.closeSocket("sessionExpired");
        amplify.publish("sessionExpired", { fromWebsocket: true });
        return;
      }

      case "sessionClosed": {
        this.closeSocket("sessionClosed");
        return;
      }

      case "websocketExpired": {
        this.closeSocket("websocketExpired");
        return;
      }

      case "push": {
        this.dispatchPush(msg);
        return;
      }

      default: {
        return;
      }
    }
  }

  private dispatchPush(msg: WebSocketPushMessage): void {
    const typedHandlers = this.handlers.get(msg.push.messageType);
    if (typedHandlers) {
      for (const handler of typedHandlers) {
        handler(msg);
      }
    }

    const allPushHandlers = this.handlers.get("AllPush");
    if (allPushHandlers) {
      for (const handler of allPushHandlers) {
        handler(msg);
      }
    }
  }

  private stringifyError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }
}

const client = new WebSocketPushClient();

export const isActive = client.isActive;
export const subscribe = client.subscribe;
export const unsubscribe = client.unsubscribe;